import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  COMMAND_PRIORITY_NORMAL,
  createCommand,
  ElementNode,
  $insertNodes,
  $createTextNode,
  $isRangeSelection,
  $getSelection,
  $isTextNode,
} from 'lexical';
import { useEffect } from 'react';
import { $getNearestNodeOfType } from '@lexical/utils';

import type { DOMConversionMap, EditorConfig, LexicalNode, NodeKey, SerializedElementNode, Spread } from 'lexical';

export type SerializedCourseCatalogueNode = Spread<
  {
    linkType: 'course' | 'programme';
    code: string;
    label: string;
  },
  SerializedElementNode
>;

export class CourseCatalogueNode extends ElementNode {
  __linkType: 'course' | 'programme';
  __code: string;
  __label: string;

  constructor(linkType: 'course' | 'programme', code: string, label: string, key?: NodeKey) {
    super(key);
    this.__linkType = linkType;
    this.__code = code;
    this.__label = label;
  }

  isInline(): boolean {
    return true;
  }

  canBeEmpty(): boolean {
    return false;
  }

  canInsertTextBefore(): boolean {
    return false;
  }

  canInsertTextAfter(): boolean {
    return true;
  }

  static clone(node: CourseCatalogueNode): CourseCatalogueNode {
    return new CourseCatalogueNode(node.__linkType, node.__code, node.__label, node.__key);
  }

  static getType(): string {
    return 'courseCatalogueLink';
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = document.createElement('a');
    element.className = config.theme.courseCatalogueLink;
    // eslint-disable-next-line no-script-url
    element.href = 'javascript:void(0);';
    element.setAttribute('data-linktype', this.__linkType);
    element.setAttribute('data-code', this.__code);
    return element;
  }

  updateDOM(): false {
    return false;
  }

  static importDOM(): DOMConversionMap<HTMLAnchorElement> | null {
    return {
      // eslint-disable-next-line id-length
      a: (domNode: HTMLAnchorElement) => {
        const linkTypeAttr = domNode.getAttribute('data-linktype');
        if (linkTypeAttr !== 'course' && linkTypeAttr !== 'programme') {
          return null;
        }
        const linkType = linkTypeAttr;
        const code = domNode.getAttribute('data-code');
        if (!code) {
          return null;
        }
        const label = domNode.textContent || '';
        if (!label) {
          return null;
        }
        return {
          conversion: () => {
            const node = $createCourseCatalogueNode(linkType, code, label);
            return { node };
          },
          priority: 2,
        };
      },
    };
  }

  static importJSON(serializedNode: SerializedCourseCatalogueNode): CourseCatalogueNode {
    const catalogueNode = $createCourseCatalogueNode(
      serializedNode.linkType,
      serializedNode.code,
      serializedNode.label
    );
    catalogueNode.setFormat(serializedNode.format);
    return catalogueNode;
  }

  exportJSON(): SerializedCourseCatalogueNode {
    return {
      ...super.exportJSON(),
      type: 'courseCatalogueLink',
      version: 1,
      linkType: this.__linkType,
      code: this.__code,
      label: this.__label,
    };
  }
}

export function $createCourseCatalogueNode(
  linkType: 'course' | 'programme',
  code: string,
  label: string = ''
): CourseCatalogueNode {
  return new CourseCatalogueNode(linkType, code, label);
}

export function $isCourseCatalogueNode(node: LexicalNode): node is CourseCatalogueNode {
  return node instanceof CourseCatalogueNode;
}

export function $getLinkTypeFromNode(node: LexicalNode): 'course' | 'programme' | null {
  if ($isCourseCatalogueNode(node)) {
    return node.__linkType;
  }
  return null;
}

export const INSERT_COURSE_CATALOGUE_LINK = createCommand('insertCourseCatalogueLink');

export const REMOVE_COURSE_CATALOGUE_LINK = createCommand('removeCourseCatalogueLink');

export function CourseCatalogueLinkPlugin(): null {
  const [editor] = useLexicalComposerContext();
  if (!editor.hasNodes([CourseCatalogueNode])) {
    throw new Error('CourseCataloguePlugin: CourseCatalogueNode is not registered on editor');
  }
  useEffect(() => {
    const insertCourseCatalogueLink = editor.registerCommand(
      INSERT_COURSE_CATALOGUE_LINK,
      (payload: { linkType: 'course' | 'programme'; code: string; label: string }) => {
        const courseCatalogueNode = $createCourseCatalogueNode(payload.linkType, payload.code, payload.label);
        const textNode = $createTextNode(payload.label);
        courseCatalogueNode.append(textNode);
        $insertNodes([courseCatalogueNode]);
        return true;
      },
      COMMAND_PRIORITY_NORMAL
    );
    const removeCourseCatalogueLink = editor.registerCommand(
      REMOVE_COURSE_CATALOGUE_LINK,
      () => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const node = selection.anchor.getNode();
          const offset = selection.anchor.offset;
          let courseCatalogueNode = $getNearestNodeOfType(node, CourseCatalogueNode);

          if (
            $isTextNode(node) &&
            offset === node.getTextContentSize() &&
            node.getNextSibling() instanceof CourseCatalogueNode
          ) {
            courseCatalogueNode = node.getNextSibling() as CourseCatalogueNode;
          }

          if (courseCatalogueNode) {
            const parent = courseCatalogueNode.getParent();
            courseCatalogueNode.remove();
            if (parent) {
              parent.append($createTextNode(' '));
            }
          }
        }
        return true;
      },
      COMMAND_PRIORITY_NORMAL
    );

    return () => {
      insertCourseCatalogueLink();
      removeCourseCatalogueLink();
    };
  }, [editor]);

  return null;
}
